import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GraphpadWidgetConfig } from '../graphpad.model';

/**
 * Preset demonstrating the intersection-area feature.
 *
 * Shows the area between two functions:
 * - f(x) = -0.5x² + 4 (downward-opening parabola)
 * - g(x) = 0.5x (linear function)
 *
 * The area between the curves is shaded with:
 * - Green regions where f(x) > g(x)
 * - Red regions where f(x) < g(x)
 * - Dashed indigo boundary lines at xStart and xEnd
 */
export const TEST_INTERSECTION_AREA_PRESET: GraphpadWidgetConfig = {
  id: 'test-intersection-area',
  type: 'graphpad',
  params: {
    f: {
      type: 'quadratic-vertex',
      params: { a: -0.5, h: 0, k: 4 },
    },
    g: {
      type: 'linear',
      params: { m: 0.5, b: 0 },
    },
    'fg-area': {
      type: 'intersectionArea',
      params: { of: ['f', 'g'], xStart: -3, xEnd: 3, signed: true },
    },
    'fg-intersection': {
      type: 'intersection',
      params: { of: ['f', 'g'] },
    },
  },
  display: {
    canvas: { color: 'neutral-0' },
    coordinateSystem: {
      boundingbox: { xStart: -6, xEnd: 6, yStart: -4, yEnd: 8 },
      grid: { type: 'lines', visible: false },
    },
    elements: {
      f: {
        curve: { color: 'indigo', strokeWidth: 3 },
      },
      g: {
        curve: { color: 'sky', strokeWidth: 3 },
      },
      'fg-area': {
        polygon: { fillOpacity: 0.3 },
        line: { color: 'indigo', dash: 3, strokeWidth: 2 },
        subparts: {
          positive: {
            polygon: { fillColor: 'green' },
          },
          negative: {
            polygon: { fillColor: 'red' },
          },
        },
      },
      'fg-intersection': {
        point: {
          color: 'amber',
          label: { contentVariant: 'name+coord', position: 'top' },
        },
      },
    },
  },
  behavior: {
    controls: [
      {
        id: 'slider-xStart',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'xStart',
        param: 'fg-area.xStart',
        range: [-5, 5, 0.5],
        defaultValue: -3,
        editable: true,
        color: 'indigo',
      },
      {
        id: 'slider-xEnd',
        type: WIDGET_CONTROL_TYPE.Slider,
        label: 'xEnd',
        param: 'fg-area.xEnd',
        range: [-5, 5, 0.5],
        defaultValue: 3,
        editable: true,
        color: 'indigo',
      },
    ],
  },
};
